import type { ValidData as AttachmentsFormValidData } from '@/components/forms/DetailedJobAttachmentsForm.vue';
import type * as openapi from '@/helpers/api/types/openapiOverrides';

import { ref } from 'vue';

import { extractImagePathForDeletion } from '@/utils/Common';
import { useJobEditStore } from '@/stores/useJobEditStore';
import {
  fetchDeleteJobStopImage,
  fetchGetJobStopImage,
  fetchPostJobStopImages,
} from '@/helpers/fetchers';

export function useJobEditPhotosData() {
  const jobEditStore = useJobEditStore();

  const stopImages = ref<openapi.GetStopImagesContentOpenapi>();
  const isFetchingPhotos = ref(false);

  async function fetchStopImages(): Promise<void> {
    if (!jobEditStore.jobData) return;
    const jobId = jobEditStore.jobData.rockhopper_id;
    const jobDate = jobEditStore.jobData.rockhopper_date;
    if (!jobId || !jobDate) return;

    try {
      stopImages.value = await fetchGetJobStopImage({
        job_id: jobId,
        job_date: jobDate,
      });
    } catch {
      stopImages.value = undefined;
    }
  }

  async function handleDeleteImage(
    stopNum: string | number,
    photoIndex: number,
  ): Promise<void> {
    if (!jobEditStore.jobData) return;
    if (!stopImages.value) return;

    const stopKey = String(stopNum);
    const photos = stopImages.value[stopKey];
    if (!photos[photoIndex]) return;

    const photo = photos[photoIndex];
    const imagePath = extractImagePathForDeletion(photo.path);

    await fetchDeleteJobStopImage({
      job_id: jobEditStore.jobData.rockhopper_id,
      job_date: jobEditStore.jobData.rockhopper_date,
      image_path: imagePath,
    });

    await fetchStopImages();
  }

  async function handleSubmitPhotos(
    formData: AttachmentsFormValidData,
    cb: () => void,
  ): Promise<void> {
    const jobData = jobEditStore.jobData;
    if (!jobData) {
      cb();
      return;
    }
    if (!formData.attachments?.length) {
      cb();
      return;
    }
    if (isFetchingPhotos.value) return;

    isFetchingPhotos.value = true;
    try {
      await fetchPostJobStopImages({
        job_id: jobData.rockhopper_id,
        job_date: jobData.rockhopper_date,
        images: formData.attachments,
        stop_number: jobData.main_stop,
        ref: 'Portal',
      });
      await fetchStopImages();
    } finally {
      cb();
      isFetchingPhotos.value = false;
    }
  }

  return {
    stopImages,
    isFetchingPhotos,
    fetchStopImages,
    handleDeleteImage,
    handleSubmitPhotos,
  };
}
